import React, { useContext, useEffect, useState } from 'react';
import Modal from '../../components/modal/modal';
import axiosInstance from '../../AxiosInstance';
import { evalToString, formatTimestamp, oseSunatToString, pagoToString, sunatToString } from '../../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheck,
    faCheckDouble,
    faClipboard,
    faDollar,
    faDownload,
    faInfo,
    faMagnifyingGlass,
    faWarning,
    faX
} from '@fortawesome/free-solid-svg-icons';
import { DangerButton, PrimaryButton } from '../../components/input/inputs';
import MyContext from '../../MyContext';
import {
    closeInvoiceDetail,
    openAcceptBill,
    openBillAttachments,
    openConfirmPayment,
    openCorrectionBill,
    openGreView,
    openObserveBill,
    openObservationText,
    openPreApproveBill,
    openRejectBill,
} from '../bus/modalApi';
import { getBillAttachedFiles } from '../bill_attachments_modal';
import { toast, ToastTypes } from '../../components/toasts/toast/toast';
import idStyles from './invoiceDetailModal.module.css';
import { PremiumCardRow } from '../shared/PremiumCardRow.jsx';
import AuditHistoryModal from '../audit_history_modal';
import { formatCurrencyAmount } from '../../utils/currency';

/** Filtro estable para historial de auditoría (pre-aprobación y aprobación definitiva). */
const BILL_EVAL_APPROVAL_AUDIT_ACTIONS = Object.freeze([
    'BILL_EVAL_PRE_APPROVED',
    'BILL_EVAL_APPROVED',
]);

function paymentPromiseErrorMessage(err) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string') {
        return detail;
    }
    return 'No se pudo actualizar la promesa de pago.';
}

function formatParty(ruc, name) {
    return `${ruc ?? ''} — ${name ?? ''}`.replace(/^[—\s]+|[—\s]+$/g, '').trim() || '-';
}

function getInvoiceIssuerLabel(invoice) {
    if (invoice?.invoice_type === 1) {
        return invoice?.purchase_order
            ? formatParty(invoice.purchase_order.provider?.ruc, invoice.purchase_order.provider?.name)
            : formatParty(invoice?.xml_supplier_ruc, invoice?.xml_supplier_name);
    }

    return formatParty(
        invoice?.retention_data?.customer_assigned_account_id,
        invoice?.retention_data?.party_name
    );
}

function getInvoiceCustomerLabel(invoice) {
    if (invoice?.invoice_type === 1) {
        return invoice?.purchase_order
            ? formatParty(invoice.purchase_order.client?.ruc, invoice.purchase_order.client?.name)
            : formatParty(invoice?.xml_customer_ruc, invoice?.xml_customer_name);
    }

    return formatParty(
        invoice?.retention_data?.r_customer_assigned_account_id,
        invoice?.retention_data?.r_party_name
    );
}

export default function InvoiceDetailModal({ show, setter, bill }) {
    const { isClient, authReady, user } = useContext(MyContext);
    const systemSettings = JSON.parse(localStorage.getItem('system_settings'));

    const [localBill, setLocalBill] = useState(bill);
    const [promiseDate, setPromiseDate] = useState('');
    const [showEvalAuditHistory, setShowEvalAuditHistory] = useState(false);

    useEffect(() => {
        setLocalBill(bill);
    }, [bill]);

    useEffect(() => {
        const d = bill?.payment_promise_date;
        setPromiseDate(d ? String(d).substring(0, 10) : '');
    }, [bill?.id, bill?.payment_promise_date]);

    const invoice = localBill;

    const savePaymentPromise = () => {
        if (!authReady || !isClient) {
            return;
        }
        axiosInstance
            .patch(`bill/${invoice.id}/payment_promise/`, {
                payment_promise_date: promiseDate || null,
            })
            .then((res) => {
                setLocalBill(res.data);
                toast('Promesa de pago guardada', ToastTypes.SUCCESS);
            })
            .catch((err) => {
                toast(paymentPromiseErrorMessage(err), ToastTypes.ERROR);
            });
    };

    const clearPaymentPromise = () => {
        if (!authReady || !isClient) {
            return;
        }
        axiosInstance
            .patch(`bill/${invoice.id}/payment_promise/`, { payment_promise_date: null })
            .then((res) => {
                setLocalBill(res.data);
                setPromiseDate('');
                toast('Promesa de pago eliminada', ToastTypes.SUCCESS);
            })
            .catch((err) => {
                toast(paymentPromiseErrorMessage(err), ToastTypes.ERROR);
            });
    };

    const downloadFile = (id, name) => {
        axiosInstance
            .get(`file/${id}/`, {
                responseType: 'blob',
            })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${name}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            });
    };

    const attachedCount = getBillAttachedFiles(invoice).length;
    const docTitle = invoice?.sn || '-';
    const hasObservations = (invoice?.observations?.length ?? 0) > 0;
    const hasCorrections = (invoice?.corrections?.length ?? 0) > 0;

    const evalStatus = invoice?.eval_status;
    const preApproveEnabled =
        (evalStatus === 0 || evalStatus === 1) && !!user?.can_pre_approve_invoice;
    const finalApproveEnabled =
        evalStatus === 4 && !!user?.can_final_approve_invoice;
    const preApproveDisabledTitle = !preApproveEnabled
        ? !user?.can_pre_approve_invoice
            ? 'No tiene permiso para pre-aprobar facturas.'
            : 'Solo disponible si el comprobante está pendiente u observado.'
        : undefined;
    const finalApproveDisabledTitle = !finalApproveEnabled
        ? !user?.can_final_approve_invoice
            ? 'No tiene permiso para aprobar facturas de forma definitiva.'
            : 'Solo disponible si el comprobante está pre-aprobado.'
        : undefined;

    const clientApprovalButtonsFragment = authReady &&
        isClient && (
            <>
                <button
                    type="button"
                    className={idStyles.footerModalBtn}
                    style={{ backgroundColor: '#0d9488' }}
                    disabled={!preApproveEnabled}
                    title={preApproveDisabledTitle}
                    aria-label={
                        !preApproveEnabled && preApproveDisabledTitle
                            ? `Pre-aprobar. ${preApproveDisabledTitle}`
                            : 'Pre-aprobar'
                    }
                    onClick={() => openPreApproveBill({ bill: invoice })}
                >
                    <FontAwesomeIcon className="pe-2" icon={faCheck} aria-hidden />
                    Pre-aprobar
                </button>
                <button
                    type="button"
                    className={idStyles.footerModalBtn}
                    style={{ backgroundColor: '#059669' }}
                    disabled={!finalApproveEnabled}
                    title={finalApproveDisabledTitle}
                    aria-label={
                        !finalApproveEnabled && finalApproveDisabledTitle
                            ? `Aprobar. ${finalApproveDisabledTitle}`
                            : 'Aprobar'
                    }
                    onClick={() => openAcceptBill({ bill: invoice })}
                >
                    <FontAwesomeIcon className="pe-2" icon={faCheckDouble} aria-hidden />
                    Aprobar
                </button>
            </>
        );

    const evalAuditInfoFooterBtn =
        authReady && invoice?.id != null ? (
            <button
                type="button"
                className={idStyles.evalAuditInfoBtn}
                onClick={() => setShowEvalAuditHistory(true)}
                title="Historial de pre-aprobaciones y aprobaciones"
                aria-label="Ver historial de pre-aprobaciones y aprobaciones en auditoría"
            >
                <FontAwesomeIcon icon={faInfo} aria-hidden />
            </button>
        ) : null;

    return (
        <>
            <Modal
                hideClose={true}
                size={'xl'}
                show={show}
                setter={setter}
                name={`Detalle del Comprobante [${docTitle}]`}
                headerClassName={idStyles.headerPremium}
                titleClassName={idStyles.titlePremium}
                dialogClassName={idStyles.dialogWide}
            >
            <div className={idStyles.bodyWrap}>
                <div className={idStyles.modalSubtitle}>Datos del comprobante</div>
                <div className={idStyles.compactGrid}>
                    <PremiumCardRow label="Tipo Comprobante:">
                        {invoice?.invoice_type === 0
                            ? 'Boleta'
                            : invoice?.invoice_type === 1
                              ? 'Factura'
                              : 'RHE'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Serie y Número:">{invoice?.sn ?? '-'}</PremiumCardRow>
                    <PremiumCardRow label="Emisor:" span2>
                        {getInvoiceIssuerLabel(invoice)}
                    </PremiumCardRow>
                    <PremiumCardRow label="Fecha de Emisión:">{invoice?.issue_date ?? '-'}</PremiumCardRow>
                    <PremiumCardRow label="Fecha de Vencimiento:">{invoice?.due_date ?? '-'}</PremiumCardRow>
                    <PremiumCardRow label="Cliente:" span2>
                        {getInvoiceCustomerLabel(invoice)}
                    </PremiumCardRow>
                    <PremiumCardRow label="Forma de Pago:">
                        {invoice?.payment_terms ? invoice.payment_terms : '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Moneda:">{invoice?.currency?.name ?? '-'}</PremiumCardRow>
                    <PremiumCardRow label="Monto Total:">
                        {formatCurrencyAmount(invoice?.currency, invoice?.total)}
                    </PremiumCardRow>
                    <PremiumCardRow label="Tipo de Operación:">
                        {invoice?.operation_type
                            ? `${invoice.operation_type.code} — ${invoice.operation_type.description}`
                            : '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Monto Detracción:">
                        {formatCurrencyAmount(invoice?.detraction_currency || invoice?.currency, invoice?.detraction_amount)}
                    </PremiumCardRow>
                    <PremiumCardRow label="Moneda Detracción:">
                        {invoice?.detraction_currency?.name ?? '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Porcentaje:">
                        {invoice?.detraction_percentage != null
                            ? `${invoice.detraction_percentage} %`
                            : '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Código BBSS:" span2>
                        {(
                            (invoice?.bbss_code != null ? String(invoice.bbss_code) : '') +
                            (invoice?.bbs_object?.description
                                ? ` — ${invoice.bbs_object.description}`
                                : '')
                        ).trim() || '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Orden de Compra:">{invoice?.purchase_order?.number ?? '-'}</PremiumCardRow>
                    <PremiumCardRow label="N° NEC:">{invoice?.order_detail?.nec_number ?? '-'}</PremiumCardRow>
                    <PremiumCardRow label="Archivos:">
                        <button
                            type="button"
                            className={idStyles.linkLike}
                            onClick={() => openBillAttachments({ bill: invoice })}
                        >
                            Ver archivos adjuntos ({attachedCount})
                        </button>
                    </PremiumCardRow>
                </div>

                <div className={idStyles.modalSubtitle}>Detalle del ítem</div>
                <table className={idStyles.modernTable}>
                    <thead>
                        <tr>
                            <th>Cantidad</th>
                            <th>Unidad de Medida</th>
                            <th>Código</th>
                            <th>Descripción</th>
                            <th>Gratuito</th>
                            <th>Valor Unitario</th>
                            <th>Valor de venta Total</th>
                            <th>Total IGV</th>
                            <th>Total Ítem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(invoice?.extra_data ?? []).map((item, index) => (
                            <tr key={index}>
                                <td>{item?.cantidad}</td>
                                <td>{item?.unidad_de_medida}</td>
                                <td>{item?.code}</td>
                                <td>{item?.descripcion}</td>
                                <td>{item?.tipo_venta === '01' ? 'NO' : 'SÍ'}</td>
                                <td>{formatCurrencyAmount(invoice?.currency, item?.valor_unitario)}</td>
                                <td>{formatCurrencyAmount(invoice?.currency, item?.precio_venta)}</td>
                                <td>{formatCurrencyAmount(invoice?.currency, item?.monto_igv)}</td>
                                <td>{formatCurrencyAmount(invoice?.currency, item?.precio_venta_unitario)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className={idStyles.modalSubtitle}>Información del Pago</div>
                {invoice && invoice?.payment_data && invoice?.payment_data?.length > 0 && (
                    <div className={idStyles.compactGrid}>
                        <PremiumCardRow label="Contado/Credito:">
                            <strong>{invoice?.payment_data[0]?.contado_credito}</strong>
                        </PremiumCardRow>
                        <PremiumCardRow label="Monto Pendiente:">
                            <strong>
                                {formatCurrencyAmount(
                                    invoice?.payment_data[0]?.monto_pendiente_pago_moneda,
                                    invoice?.payment_data[0]?.monto_pendiente_pago
                                )}
                            </strong>
                        </PremiumCardRow>
                        <PremiumCardRow label="Moneda Monto Pendiente:">
                            <strong>{invoice?.payment_data[0]?.monto_pendiente_pago_moneda}</strong>
                        </PremiumCardRow>
                    </div>
                )}
                <table className={idStyles.modernTable}>
                    <thead>
                        <tr>
                            <th>N° Cuota</th>
                            <th>Fecha de Vencimiento Cuota</th>
                            <th>Monto Cuota</th>
                            <th>Moneda Cuota</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(invoice?.payment_data ?? [])
                            .filter((item) => item?.id_cuota !== 'Credito' && item?.id_cuota !== 'Contado')
                            .map((item, index) => (
                                <tr key={index}>
                                    <td>{item?.id_cuota}</td>
                                    <td>{item?.fecha_pago_cuota}</td>
                                    <td>
                                        {formatCurrencyAmount(
                                            item?.monto_pago_unico_cuota_moneda,
                                            item?.monto_pago_unico_cuota
                                        )}
                                    </td>
                                    <td>{item?.monto_pago_unico_cuota_moneda}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>

                <div className={idStyles.modalSubtitle}>Retenciones de IGV</div>
                <div className={idStyles.compactGrid5}>
                    <PremiumCardRow label="Indicador Cargo/Descuento:" column>
                        {invoice?.retention_data?.indicador_cargo_descuento ?? '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Código de motivo:" column>
                        {invoice?.retention_data?.codigo_motivo_cargo_descuento ?? '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Porcentaje de Retención:" column>
                        {invoice?.retention_data?.porcentaje_retencion ?? '-'}
                    </PremiumCardRow>
                    <PremiumCardRow label="Importe Retención:" column>
                        {formatCurrencyAmount(
                            invoice?.retention_data?.importe_retencion_moneda,
                            invoice?.retention_data?.importe_retencion
                        )}
                    </PremiumCardRow>
                    <PremiumCardRow label="Moneda Importe Retención:" column>
                        {invoice?.retention_data?.importe_retencion_moneda ?? '-'}
                    </PremiumCardRow>
                </div>

                <div className={idStyles.modalSubtitle}>Guías de Remisión</div>
                <table className={idStyles.modernTable}>
                    <thead>
                        <tr>
                            <th>N°</th>
                            <th>Fecha de Emisión</th>
                            <th>Remitente</th>
                            <th>Destinatario</th>
                            <th>Detalle</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!invoice?.gres || invoice.gres.length === 0 ? (
                            <tr>
                                <td colSpan={5} className={idStyles.emptyCell}>
                                    No hay guías de remisión
                                </td>
                            </tr>
                        ) : (
                            invoice.gres.map((item, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>{item?.data?.fecha_emision}</td>
                                    <td>{item?.data?.remitente}</td>
                                    <td>{item?.data?.destinatario}</td>
                                    <td>
                                        <div className="d-flex justify-content-center">
                                            <div
                                                role="button"
                                                onClick={() => {
                                                    openGreView({ greData: item.data });
                                                }}
                                            >
                                                <div
                                                    className={idStyles.actionBtn}
                                                    style={{
                                                        backgroundColor: systemSettings?.main_color
                                                            ? systemSettings.main_color
                                                            : '#FF1235',
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faMagnifyingGlass}
                                                        color="#FFF"
                                                        size="sm"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <div className={idStyles.modalSubtitle}>Estados</div>
                <div className={idStyles.statusGrid}>
                    <div className={idStyles.cardCol}>
                        <div className={idStyles.label}>Estado OSE/SUNAT</div>
                        <div>
                            {invoice?.invoice_type === 1
                                ? oseSunatToString(invoice?.sunat_status)
                                : oseSunatToString('1')}
                        </div>
                    </div>
                    <div className={idStyles.cardCol}>
                        <div className={idStyles.label}>Estado conformidad</div>
                        <div>
                            {invoice?.invoice_type === 1
                                ? sunatToString(invoice?.conformity_status)
                                : sunatToString('04')}
                        </div>
                    </div>
                    <div className={idStyles.cardCol}>
                        <div className={idStyles.label}>Estado evaluación</div>
                        <div>{evalToString(invoice?.eval_status)}</div>
                    </div>
                    <div className={idStyles.cardCol}>
                        <div className={idStyles.label}>Estado pago</div>
                        <div>{pagoToString(invoice?.payment_status)}</div>
                    </div>
                </div>

                {invoice?.payment_confirmation_observations ? (
                    <>
                        <div className={idStyles.modalSubtitle} style={{ marginTop: '1rem' }}>
                            Observaciones al confirmar pago
                        </div>
                        <div className={idStyles.paymentObservationsBox}>
                            {invoice.payment_confirmation_observations}
                        </div>
                    </>
                ) : null}

                {hasObservations ? (
                    <>
                        <div className={idStyles.modalSubtitle}>Observaciones del Cliente</div>
                        <table className={idStyles.modernTable}>
                            <thead>
                                <tr>
                                    <th>Fecha de Subida</th>
                                    <th>Tipo de Documento</th>
                                    <th>Nombre de Archivo</th>
                                    <th>Ver</th>
                                    <th>Descargar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.observations.map((observation, oIndex) =>
                                    observation.files && observation.files.length > 0 ? (
                                        observation.files.map((file, fileIndex) => (
                                            <tr key={`o-${oIndex}-f-${fileIndex}`}>
                                                <td>{formatTimestamp(observation.created_at)}</td>
                                                <td>{file.type}</td>
                                                <td>{file.name}</td>
                                                <td
                                                    role="button"
                                                    onClick={() =>
                                                        openObservationText({ text: observation.text })
                                                    }
                                                >
                                                    <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
                                                </td>
                                                <td
                                                    role="button"
                                                    onClick={() => downloadFile(file.id, file.name)}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} size="sm" />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr key={`o-${oIndex}`}>
                                            <td>{formatTimestamp(observation.created_at)}</td>
                                            <td>-</td>
                                            <td>-</td>
                                            <td
                                                role="button"
                                                onClick={() =>
                                                    openObservationText({ text: observation.text })
                                                }
                                            >
                                                <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
                                            </td>
                                            <td></td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </>
                ) : null}

                {hasCorrections ? (
                    <>
                        <div className={idStyles.modalSubtitle}>Correcciones del Proveedor</div>
                        <table className={idStyles.modernTable} style={{ marginBottom: 12 }}>
                            <thead>
                                <tr>
                                    <th>Fecha de Subida</th>
                                    <th>Tipo de Documento</th>
                                    <th>Nombre de Archivo</th>
                                    <th>Ver</th>
                                    <th>Descargar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.corrections.map((correction, cIndex) =>
                                    correction.files && correction.files.length > 0 ? (
                                        correction.files.map((file, fileIndex) => (
                                            <tr key={`c-${cIndex}-f-${fileIndex}`}>
                                                <td>{formatTimestamp(correction.created_at)}</td>
                                                <td>{file.type}</td>
                                                <td>{file.name}</td>
                                                <td
                                                    role="button"
                                                    onClick={() =>
                                                        openObservationText({ text: correction.text })
                                                    }
                                                >
                                                    <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
                                                </td>
                                                <td
                                                    role="button"
                                                    onClick={() => downloadFile(file.id, file.name)}
                                                >
                                                    <FontAwesomeIcon icon={faDownload} size="sm" />
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr key={`c-${cIndex}`}>
                                            <td>{formatTimestamp(correction.created_at)}</td>
                                            <td>-</td>
                                            <td>-</td>
                                            <td
                                                role="button"
                                                onClick={() =>
                                                    openObservationText({ text: correction.text })
                                                }
                                            >
                                                <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
                                            </td>
                                            <td></td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </>
                ) : null}

                <div className={idStyles.sectionPromesa}>
                    <div className={idStyles.modalSubtitle} style={{ marginTop: 0 }}>
                        Promesa de pago
                    </div>
                    <div className={idStyles.compactGrid}>
                        {authReady && isClient ? (
                            <div className={idStyles.compactGridSpan2} style={{ maxWidth: '360px' }}>
                                <div className={idStyles.label}>Fecha prometida de pago</div>
                                <div>
                                    <input
                                        type="date"
                                        className="form-control rounded-2"
                                        style={{ backgroundColor: '#D9D9D9', border: '1px solid #ccc' }}
                                        value={promiseDate}
                                        onChange={(e) => setPromiseDate(e.target.value)}
                                    />
                                    <div className={`d-flex flex-wrap align-items-center gap-2 mt-2`}>
                                        <PrimaryButton variant="quiet" name="Guardar" onClick={savePaymentPromise} />
                                        <DangerButton variant="destructive" name="Quitar" onClick={clearPaymentPromise} />
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <PremiumCardRow label="Promesa de pago:">
                                {authReady ? invoice?.payment_promise_date || '-' : '-'}
                            </PremiumCardRow>
                        )}
                    </div>
                </div>
            </div>

            <div className={idStyles.footerBar}>
                {!authReady ? (
                    <>
                        <div className={idStyles.footerLeft}>{evalAuditInfoFooterBtn}</div>
                        <div className={idStyles.footerRight}>
                            <DangerButton onClick={() => setter(false)} name="Cerrar" />
                        </div>
                    </>
                ) : isClient ? (
                    invoice?.eval_status === 3 ? (
                        <>
                            <div className={idStyles.footerLeft}>{evalAuditInfoFooterBtn}</div>
                            <div className={idStyles.footerRight}>
                                {clientApprovalButtonsFragment}
                                <DangerButton onClick={() => setter(false)} name="Cerrar" />
                            </div>
                        </>
                    ) : invoice?.eval_status === 0 ||
                      invoice?.eval_status === 1 ||
                      invoice?.eval_status === 4 ? (
                        <>
                            <div className={idStyles.footerLeft}>{evalAuditInfoFooterBtn}</div>
                            <div className={idStyles.footerRight}>
                                {clientApprovalButtonsFragment}
                                <button
                                    type="button"
                                    className={idStyles.footerModalBtn}
                                    style={{ backgroundColor: '#0891b2' }}
                                    onClick={() => {
                                        closeInvoiceDetail();
                                        openObserveBill({ bill: invoice });
                                    }}
                                >
                                    <FontAwesomeIcon className="pe-2" icon={faWarning} aria-hidden />
                                    Observar
                                </button>
                                <button
                                    type="button"
                                    className={idStyles.footerModalBtn}
                                    style={{ backgroundColor: '#b91c1c' }}
                                    onClick={() => openRejectBill({ bill: invoice })}
                                >
                                    <FontAwesomeIcon className="pe-2" icon={faX} aria-hidden />
                                    Rechazar
                                </button>
                                <DangerButton onClick={() => setter(false)} name="Cerrar" />
                            </div>
                        </>
                    ) : invoice?.eval_status === 2 ? (
                        <>
                            <div className={idStyles.footerLeft}>{evalAuditInfoFooterBtn}</div>
                            <div className={idStyles.footerRight}>
                                {clientApprovalButtonsFragment}
                                {invoice?.payment_status === 0 && (
                                    <button
                                        type="button"
                                        className={`${idStyles.footerModalBtn} ${idStyles.footerModalBtnAmber}`}
                                        onClick={() => openConfirmPayment({ bill: invoice })}
                                    >
                                        <FontAwesomeIcon icon={faDollar} className="pe-2" aria-hidden /> Confirmar Pago
                                    </button>
                                )}
                                <DangerButton onClick={() => setter(false)} name="Cerrar" />
                            </div>
                        </>
                    ) : null
                ) : (
                    <>
                        <div className={idStyles.footerLeft}>{evalAuditInfoFooterBtn}</div>
                        <div className={idStyles.footerRight}>
                            {invoice?.eval_status === 1 && (
                                <button
                                    type="button"
                                    className={idStyles.footerModalBtn}
                                    style={{ backgroundColor: '#4A4DBB' }}
                                    onClick={() => {
                                        setter(false);
                                        openCorrectionBill({ bill: invoice });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faClipboard} className="pe-2" aria-hidden />
                                    Subsanar
                                </button>
                            )}
                            <DangerButton onClick={() => setter(false)} name="Cerrar" />
                        </div>
                    </>
                )}
            </div>
            </Modal>
            <AuditHistoryModal
                show={showEvalAuditHistory}
                onHide={() => setShowEvalAuditHistory(false)}
                mode="bill"
                entityId={invoice?.id}
                titleLabel={docTitle}
                actionsFilter={BILL_EVAL_APPROVAL_AUDIT_ACTIONS}
            />
        </>
    );
}
